import {useGTMDispatch} from './react-gtm-hook'
import {format, isToday, isTomorrow, parseISO} from 'date-fns'
import { slotsMatch } from '../utils/booking-utils'

const formatDeliveryDate = (windowStartTime) => {
  if (!windowStartTime) return ''

  const date = new Date(windowStartTime)
  if (Number.isNaN(date.getTime())) return ''

  return format(date, 'dd-MM-yyyy')
}

const mapShippingTier = (deliveryDay, slots) => {
  if (deliveryDay?.isRapid) {
    return 'Rapid'
  }

  if (deliveryDay?.isPreBook) {
    return 'Pre Book Reservation'
  }

  const slotDateString = slots?.[0]?.windowStartTime
  const slotDate = parseISO(slotDateString)

  if (isToday(slotDate)) {
    return 'Same Day'
  }

  if (isTomorrow(slotDate)) {
    return 'Next day'
  }

  return 'Future day'
}

const mapSlotStatus = (slot, currentDeliverySlotId) => {
  if (currentDeliverySlotId && slotsMatch(slot.id, currentDeliverySlotId)) {
    return 'Booked'
  }

  if (!slot.available) {
    return mapUnavailableStatus(slot)
  }

  return 'Available'
}

const mapUnavailableStatus = (slot) => {
  let message = 'Unavailable'

  if (slot.sameDayPastCutOff) {
    return message + ' - Cutoff'
  }

  if (slot.notReleasedStatus) {
    return message + ' - Not Released'
  }

  return message
}

const createSlotFromPreBookOrder = (preBookOrder) => {
  if (!preBookOrder) {
    return null
  }

  return {
    id: preBookOrder.c_deliverySlotId ?? '',
    storeId: preBookOrder.c_deliveryStoreId ?? '',
    shippingCost: preBookOrder.orderTotal,
    preBookShippingCost: preBookOrder.orderTotal,
    windowStartTime: preBookOrder.c_windowStartTime ?? '',
    windowEndTime: preBookOrder.c_windowEndTime ?? '',
    startHour: preBookOrder.c_preBookSlotStart,
    endHour: preBookOrder.c_preBookSlotEnd,
    available: true,
    isPrebookSlot: true,
  }
}

const flattenSlots = (slots = []) =>
  slots.flatMap((item) => (Array.isArray(item?.slots) ? item.slots : [item]))

const normalizeViewSlots = ({deliveryDay, slots, prebookedOrder}) => {
  if (Array.isArray(slots) && slots.length > 0) {
    return deliveryDay?.isPreBook ? flattenSlots(slots) : slots
  }

  if (deliveryDay?.isPreBook && prebookedOrder) {
    const prebookSlot = createSlotFromPreBookOrder(prebookedOrder)
    if (prebookSlot?.id) {
      return [prebookSlot]
    }
  }

  return []
}

export const useViewShippingDetailsTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const sendViewShippingDetailsData = (data) => {
    if (!data) {
      return null
    }

    const {deliveryDay, slots = [], prebookedOrder, currentDeliverySlotId} = data
    if (!deliveryDay) {
      return null
    }

    const normalizedSlots = normalizeViewSlots({deliveryDay, slots, prebookedOrder})

    if (normalizedSlots.length === 0) {
      return null
    }

    const shippingTier = mapShippingTier(deliveryDay, normalizedSlots)
    const deliverySlots = normalizedSlots.map((slot) => ({
      store_id: slot.storeId ?? '',
      value: slot.isPrebookSlot ? slot.preBookShippingCost ?? slot.shippingCost ?? 0 : slot.shippingCost ?? 0,
      delivery_date: formatDeliveryDate(slot.windowStartTime),
      delivery_slot_start: slot.startHour ?? '',
      delivery_slot_end: slot.endHour ?? '',
      delivery_slot_status: mapSlotStatus(slot, currentDeliverySlotId),
    }))


    setTimeout(() => {
      try {
        sendDataToGTM({
          event: 'view_shipping_details',
          ecommerce: {
            shipping_tier: shippingTier,
            delivery_slots: deliverySlots,
          }
        })
      } catch (error) {
        console.error('useViewShippingDetailsTracking error within setTimeout: ' + error.message)
      }
    }, 0)
    
  }
  return sendViewShippingDetailsData
}

export const useAddShippingDetailsTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const sendAddShippingDetailsData = (data) => {
    if (!data) {
      return null
    }

    const {deliveryDay, slot, currentDeliverySlotId, isRebook} = data
    if (!deliveryDay || !slot) {
      return null
    }

    const shippingTier = mapShippingTier(deliveryDay, [slot])
    const deliverySlotStatus = isRebook ? 'Rebooked' : mapSlotStatus(slot, currentDeliverySlotId)

    setTimeout(() => {
      try {
        sendDataToGTM({
          event: 'add_shipping_details',
          ecommerce: {
            shipping_tier: shippingTier,
            store_id: slot.storeId ?? '',
            value: slot.isPrebookSlot ? slot.preBookShippingCost : slot.shippingCost,
            delivery_date: formatDeliveryDate(slot.windowStartTime),
            delivery_slot_start: slot.startHour ?? '',
            delivery_slot_end: slot.endHour ?? '',
            delivery_slot_status: deliverySlotStatus,
          }
        })
      } catch (error) {
        console.error('useAddShippingDetailsTracking error within setTimeout: ' + error.message)
      }
    }, 0)
    
  }
  return sendAddShippingDetailsData
}